import { ErrorBoundary, ErrorBoundaryContext } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { PartnerIntegrationModalProvider } from 'lib-integrations-partner-widgets';
import { MetaConversation } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { useState } from 'react';

import { FlatCardContext } from '../../hooks/useFlatCardContext/useFlatCardContext';
import { useSidebarCards } from '../../hooks/useSidebarCards/useSidebarCards';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { SidebarCardFallback } from '../sidebarCardFallback';
import { SidebarCardsSummary } from '../sidebarCardsSummary/sidebarCardsSummary';

import styles from './flatSidebar.module.scss';

type FlatSidebarProps = {
  readonly metaConversation: MetaConversation;
  readonly isMinimized: boolean;
  readonly isCardOpen: (key: string) => boolean;
  readonly toggleCard: (key: string) => void;
};

export const FlatSidebar = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
}: FlatSidebarProps): JSX.Element => {
  const sidebarMode = useSidebarMode();
  // Temporarily reveals hidden cards (dimmed) from the summary's show/hide link.
  const [forceShowHidden, setForceShowHidden] = useState(false);
  const {
    orderedCards,
    visibleCards,
    isHidden,
    isUserHidden,
    isConditionHidden,
    toggleVisibility,
    reorder,
    dragType,
    hiddenCount,
    conditionHiddenCount,
  } = useSidebarCards({
    metaConversation,
    isCardOpen,
    toggleCard,
  });

  // Show hidden cards while customizing or when the agent force-shows them.
  const revealHidden = sidebarMode === 'customization' || forceShowHidden;
  const list = revealHidden ? orderedCards : visibleCards;

  return (
    <PartnerIntegrationModalProvider>
      <div
        className={classnames(styles.cardList, {
          [styles.isMinimized]: isMinimized,
        })}
      >
        {list.map((card, index) => {
          const hidden = isHidden(card.key);
          const rendered = card.render({
            index,
            isMinimized,
            type: dragType,
            onMove: reorder,
          });

          /*
           * Replaces the app-wide handler instead of passing `onError` as a
           * prop: the boundary calls both, so a prop would capture the same
           * error twice.
           */
          const guarded = (
            <ErrorBoundaryContext.Provider
              value={{
                onError: (error) =>
                  Sentry.captureException(error, {
                    ...(card.cardType === 'native'
                      ? { tags: { team: SentryTeam.Partners } }
                      : {}),
                    extra: {
                      cardLabel: card.label,
                      cardType: card.cardType,
                      cardId: card.cardId,
                    },
                  }),
              }}
            >
              <ErrorBoundary
                fallback={<SidebarCardFallback label={card.label} />}
              >
                {rendered}
              </ErrorBoundary>
            </ErrorBoundaryContext.Provider>
          );

          return (
            <FlatCardContext.Provider
              key={card.key}
              value={{
                cardKey: card.key,
                isHidden: hidden,
                isUserHidden: isUserHidden(card.key),
                isConditionHidden: isConditionHidden(card.key),
                toggleVisibility: () => toggleVisibility(card.key),
              }}
            >
              {revealHidden && hidden ? (
                <div className={styles.dimmed}>{guarded}</div>
              ) : (
                guarded
              )}
            </FlatCardContext.Provider>
          );
        })}
        {/* Hidden-cards summary as the last card. Customization already reveals
            every card, and the minimized rail has no room for the banner. */}
        {!isMinimized && sidebarMode === 'default' && (
          <SidebarCardsSummary
            hiddenCount={hiddenCount}
            conditionHiddenCount={conditionHiddenCount}
            isForceShown={forceShowHidden}
            onToggle={() => setForceShowHidden((prev) => !prev)}
          />
        )}
      </div>
    </PartnerIntegrationModalProvider>
  );
};
